import { useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Breadcrumbs, PageShell } from "@/components/layout/PageShell";
import { MenuItemImage } from "@/components/menu/MenuItemImage";
import { PriceDisplay } from "@/components/menu/PriceDisplay";
import { DiscountBadge } from "@/components/menu/DiscountBadge";
import { Button } from "@/components/ui/button";
import { ProductDetailConfigurator } from "@/components/menu/ProductCard";
import { EmptyState, ErrorState, Shimmer, Spinner } from "@/components/states";
import { useI18n } from "@/lib/i18n";
import { useCart } from "@/lib/cart";
import { useBranchOrdering } from "@/lib/use-branch-ordering";
import { BranchOrderNotice } from "@/components/BranchOrderNotice";
import { menuItemQuery } from "@/lib/queries";
import { loc } from "@/lib/format";
import type { MenuItem } from "@/lib/types";
import { CustomerErrorViewer } from "@/components/customer-error-viewer";
import { SimilarItemsCarousel } from "@/components/menu/SimilarItemsCarousel";
import { categoryItemsQuery } from "@/lib/queries";
import { similarMenuItems } from "@/lib/menu-model";

export const Route = createFileRoute("/menu/$slug")({
  component: ProductDetails,
});

function ProductDetails() {
  const { slug } = Route.useParams();
  const [variationId, setVariationId] = useState("");
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const { cart, hydrated } = useCart();
  const { branch, block } = useBranchOrdering();
  const branchId = cart.orderType?.branchId ?? "";
  const orderType = cart.orderType?.type;
  const { data, error, isPending, isError, refetch } = useQuery(
    menuItemQuery(slug, branchId, orderType),
  );
  const { data: categoryItems } = useQuery(
    categoryItemsQuery(data?.categoryId ?? "", branchId, orderType ?? "pickup"),
  );

  function requireOrderType() {
    if (cart.orderType) return true;
    navigate({
      to: "/order-type",
      search: { returnTo: "/menu", productSlug: slug },
    });
    return false;
  }

  if (!hydrated) {
    return (
      <PageShell>
        <p className="flex items-center justify-center gap-2 py-16 text-sm text-muted-foreground">
          <Spinner /> {t("common.loading")}
        </p>
      </PageShell>
    );
  }

  if (isPending) {
    return (
      <PageShell>
        <div className="grid gap-6 md:grid-cols-2">
          <Shimmer className="h-80 rounded-3xl" />
          <div className="grid content-start gap-4">
            <Shimmer className="h-10 w-2/3" />
            <Shimmer className="h-24" />
            <Shimmer className="h-12 w-1/2" />
          </div>
        </div>
      </PageShell>
    );
  }

  if (isError) {
    return (
      <PageShell>
        <ErrorState error={error} onRetry={() => refetch()} />
      </PageShell>
    );
  }

  if (!data) {
    return (
      <PageShell>
        <EmptyState
          title={t("notfound.title")}
          description={t("notfound.desc")}
          action={
            <Button asChild>
              <Link to="/menu">{t("cart.browse")}</Link>
            </Button>
          }
        />
      </PageShell>
    );
  }

  const variation =
    data.type === "variable"
      ? (data.variations.find((option) => option.id === variationId) ??
        data.variations[0])
      : undefined;
  const displayPrice = variation?.price ?? data.price;
  const productImage =
    variation?.responsiveImage ??
    (variation?.image || data.responsiveImage || data.image);

  return (
    <PageShell className="max-w-6xl">
      <Breadcrumbs
        items={[
          { label: t("nav.home"), to: "/" },
          { label: t("nav.menu"), to: "/menu" },
          { label: loc(data.name, lang) },
        ]}
      />
      <div className="mt-5 grid items-start gap-6 lg:grid-cols-[minmax(0,0.95fr)_minmax(0,1.05fr)] lg:gap-10">
        <div className="relative overflow-hidden rounded-3xl bg-muted lg:sticky lg:top-24">
          <MenuItemImage
            source={productImage}
            name={loc(data.name, lang)}
            sizes="(max-width: 639px) calc(100vw - 2rem), (max-width: 1023px) calc(100vw - 3rem), 520px"
            className="aspect-[4/3] w-full max-h-[34rem] object-cover sm:aspect-[5/4] lg:aspect-square"
          />
          {cart.orderType && (
            <DiscountBadge
              price={displayPrice}
              originalPrice={variation?.originalPrice ?? data.originalPrice}
              className="end-3 top-3 px-3 py-2 text-sm sm:end-4 sm:top-4"
            />
          )}
        </div>
        <div className="grid min-w-0 content-start gap-6">
          <div className="grid gap-3 pb-5">
            <h1 className="text-3xl font-black leading-tight tracking-tight sm:text-4xl">
              {loc(data.name, lang)}
            </h1>
            {loc(data.description, lang) ? (
              <p className="max-w-prose text-base leading-relaxed text-muted-foreground">
                {loc(data.description, lang)}
              </p>
            ) : null}
            {cart.orderType ? (
              <p className="text-2xl font-black tabular-nums text-foreground">
                <PriceDisplay
                  price={displayPrice}
                  originalPrice={variation?.originalPrice ?? data.originalPrice}
                />
              </p>
            ) : null}
          </div>

          {!cart.orderType ? (
            <ProductOrderabilityNotice
              reason="selection_required"
              onSelect={requireOrderType}
            />
          ) : block ? (
            <BranchOrderNotice reason={block} />
          ) : !data.onlineAvailable ? (
            <ProductOrderabilityNotice
              reason={data.orderabilityReason ?? "not_available_at_branch"}
              onSelect={() =>
                navigate({
                  to: "/order-type",
                  search: { returnTo: "/menu", productSlug: slug },
                })
              }
            />
          ) : (
            <ProductDetailConfigurator
              key={data.id}
              item={data}
              variation={variation}
              onVariationChange={setVariationId}
              onRequireOrderType={requireOrderType}
            />
          )}
        </div>
      </div>
      <SimilarItemsCarousel
        items={similarMenuItems(categoryItems ?? [], data)}
      />
    </PageShell>
  );
}

export function ProductOrderabilityNotice({
  reason,
  onSelect,
}: {
  reason: NonNullable<MenuItem["orderabilityReason"]>;
  onSelect: () => void;
}) {
  const { t } = useI18n();
  const selectionRequired = reason === "selection_required";
  const message =
    reason === "branch_unavailable"
      ? t("menu.branchUnavailable")
      : reason === "not_available_for_order_type"
        ? t("menu.orderTypeUnavailable")
        : selectionRequired
          ? t("menu.selectOrderContext")
          : t("menu.unavailableAtBranch");
  if (!selectionRequired) {
    const title =
      reason === "branch_unavailable"
        ? t("errors.branchUnavailableTitle")
        : reason === "not_available_for_order_type"
          ? t("errors.orderTypeUnavailableTitle")
          : t("errors.itemUnavailableTitle");
    return (
      <CustomerErrorViewer
        details={{ code: reason, title, message }}
        compact
        actionLabel={t("menu.changeOrderContext")}
        onAction={onSelect}
      />
    );
  }
  return (
    <div className="grid gap-3 rounded-2xl bg-muted px-4 py-4">
      <p className="text-sm font-bold text-muted-foreground">{message}</p>
      <Button variant="default" className="w-fit" onClick={onSelect}>
        {t("menu.selectOrderContextAction")}
      </Button>
    </div>
  );
}
